"use client";

import { usePathname } from "next/navigation";
import { useEffect, useRef } from "react";
import { captureCreatorAcquisition } from "@/lib/analytics/creatorAcquisition";
import {
  canUseCreatorMetaPixel,
  initMetaPixel,
  resolveMetaPixelId,
  trackMetaPixelEvent,
} from "@/lib/analytics/metaPixel";

/**
 * Loads the creator-acquisition pixel for the /join funnel and reports a
 * PageView for each page in it.
 *
 * The host gate is read here rather than during render because it depends on
 * `window`: deciding server-side would either mean trusting a forwarded Host
 * header or hydrating a different tree than was rendered. An effect sidesteps
 * both — it only ever runs in the browser, where the answer is unambiguous.
 *
 * Mounted by the /join layout, so it covers the landing page and the form and
 * nothing else.
 *
 * Except a Creator Marketplace invite: its URL carries a single-use token,
 * which a PageView would send to Meta in the page address, and the creator
 * came from a staff message rather than an ad, so it isn't funnel traffic.
 */
export function MetaPixel() {
  const pathname = usePathname();
  const initialised = useRef(false);

  useEffect(() => {
    if (!canUseCreatorMetaPixel()) return;
    const pixelId = resolveMetaPixelId(window.location.hostname);
    if (!pixelId) return;
    captureCreatorAcquisition();

    if (!initialised.current) {
      initMetaPixel(pixelId);
      initialised.current = true;
    }

    // fbevents.js reports nothing on its own, so this also covers the client
    // navigation from /join to /join/apply — no document load, no automatic
    // PageView, and the step someone reaches is the whole funnel signal.
    trackMetaPixelEvent("PageView");
  }, [pathname]);

  return null;
}
